import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ActionModal } from 'br2s-ui-componentlibrary_r2_02';
import { LocalStorageService } from 'br2s-ui-common-utils-02';
import { useStore } from '@/redux/store';
import {
  isProfileSwitchNavigateDenied,
  toggleEntityChange,
} from '@/pages/ProfileSelection/profileSelection-utilities';
import Loading from '../Loading';

type ModalType = 'switched' | 'exitDenied';

const UNGUARDED_URLS = ['profile-selection', 'authenticate', 'mockspcp'];

const ProfileSwitchGuard = () => {
  const location = useLocation();
  const { auth } = useStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>('switched');
  const navigate = useNavigate();

  const isSwitchDenied =
    isProfileSwitchNavigateDenied() &&
    UNGUARDED_URLS.every((url) => !location.pathname.includes(url));

  useEffect(() => {
    if (!auth.authenticated) return;

    if (isSwitchDenied) {
      showModal('exitDenied');
    }

    const storageListener = (event: StorageEvent) => {
      if (
        event.key === 'entityChanged' &&
        event.newValue === 'true' &&
        document.hidden
      ) {
        showModal('switched');
      }
    };

    const denyExitListener = () => {
      showModal('exitDenied');
    };

    window.addEventListener('storage', storageListener);
    window.addEventListener('switchExitDenied', denyExitListener);

    return () => {
      window.removeEventListener('storage', storageListener);
      window.removeEventListener('switchExitDenied', denyExitListener);
    };
  }, [auth.authenticated, location.pathname]);

  const showModal = (type: ModalType) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleClick = () => {
    toggleEntityChange('false');
    setIsModalOpen(false);
    navigate('/');
  };

  const modalContent = {
    switched: {
      title: 'You have switched profile',
      message: 'We have detected that you have switched profile.',
      btnLabel: 'Go to homepage',
      onClick: handleClick,
    },
    exitDenied: {
      title: 'Select a profile',
      message: 'Choose an entity profile to proceed with the transaction.',
      btnLabel: 'Select profile',
      onClick: () => setIsModalOpen(false),
    },
  };

  if (!LocalStorageService.hasAuth())
    return (
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    );

  return (
    <>
      {isSwitchDenied ? (
        <Navigate to="profile-selection" />
      ) : (
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      )}
      <ActionModal
        id="header-modal"
        isOpen={isModalOpen}
        title={modalContent[modalType].title}
        message={modalContent[modalType].message}
        state="warning"
        actionButton2={{
          id: 'profile-ok',
          type: 'primary',
          label: modalContent[modalType].btnLabel,
          onClick: modalContent[modalType].onClick,
        }}
        onCloseClick={modalContent[modalType].onClick}
      />
    </>
  );
};

export default ProfileSwitchGuard;
